import React, { useState, useEffect, useRef } from "react";
import type { PresenceElement } from "./types";
import AgentIcon from "./AgentIcon";
import VoiceInput from "../chat/VoiceInput";

interface WhisperProps {
  element: PresenceElement;
  onDismiss: (id: string) => void;
  onReply?: (elementId: string, text: string) => void;
}

/**
 * Ephemeral floating agent message — appears, lingers, fades.
 * Solid surface background with accent left-border for strong visibility.
 * Click to reply inline.
 */
export default function Whisper({ element, onDismiss, onReply }: WhisperProps): React.ReactElement {
  const [opacity, setOpacity] = useState(0);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fadeIn = setTimeout(() => setOpacity(1), 50);
    timersRef.current.push(fadeIn);

    if (!replyOpen) {
      const duration = element.duration ?? 10000;
      const fadeOut = setTimeout(() => setOpacity(0), Math.max(duration - 1000, 500));
      const remove = setTimeout(() => onDismiss(element.id), duration);
      timersRef.current.push(fadeOut, remove);
    }

    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, [element.id, element.duration, onDismiss, replyOpen]);

  useEffect(() => {
    if (replyOpen) inputRef.current?.focus();
  }, [replyOpen]);

  const handleReplySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    onReply?.(element.id, replyText.trim());
    setReplyText("");
    setReplyOpen(false);
  };

  const handleVoiceTranscript = (transcript: string) => {
    const next = String(transcript || "").trim();
    if (!next) return;
    setReplyText((prev) => (prev.trim() ? `${prev.trim()} ${next}` : next));
    inputRef.current?.focus();
  };

  const handleWhisperClick = () => {
    if (!replyOpen && onReply) {
      setReplyOpen(true);
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      style={{
        maxWidth: 320,
        borderRadius: 10,
        opacity,
        transition: "opacity 0.5s ease",
        cursor: onReply && !replyOpen ? "pointer" : "default",
        backgroundColor: "var(--c-surface-2)",
        borderLeft: "3px solid var(--c-primary)",
        border: "1px solid var(--c-border-hover)",
        borderLeftWidth: 3,
        borderLeftColor: "var(--c-primary)",
        boxShadow: "0 4px 16px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.08)",
        pointerEvents: onReply ? "auto" : "none",
      }}
      onClick={handleWhisperClick}
    >
      {/* Agent identity + text */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          padding: "12px 14px",
        }}
      >
        <AgentIcon size={14} />
        <p
          style={{
            margin: 0,
            fontSize: 13,
            lineHeight: 1.5,
            color: "var(--c-text)",
            flex: 1,
          }}
        >
          {element.text}
        </p>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setOpacity(0);
            setTimeout(() => onDismiss(element.id), 250);
          }}
          style={{
            flexShrink: 0,
            padding: 2,
            borderRadius: 4,
            border: "none",
            backgroundColor: "transparent",
            color: "var(--c-text-faint)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "auto",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "var(--c-text-secondary)";
            e.currentTarget.style.backgroundColor = "var(--c-hover)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = "var(--c-text-faint)";
            e.currentTarget.style.backgroundColor = "transparent";
          }}
          title="Dismiss"
          aria-label="Dismiss notification"
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M8 2L2 8M2 2l6 6" />
          </svg>
        </button>
      </div>

      {/* Reply input */}
      {replyOpen && (
        <form
          onSubmit={handleReplySubmit}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setReplyOpen(false);
              setReplyText("");
            }
          }}
          style={{ padding: "0 10px 10px" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input
              ref={inputRef}
              type="text"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Reply..."
              style={{
                flex: 1,
                width: "auto",
                padding: "8px 10px",
                borderRadius: 6,
                fontSize: 12,
                outline: "none",
                backgroundColor: "var(--c-surface-3)",
                color: "var(--c-text)",
                border: "1px solid var(--c-border-hover)",
                boxSizing: "border-box",
              }}
            />
            <VoiceInput onTranscript={handleVoiceTranscript} />
          </div>
        </form>
      )}

      {/* Reply hint */}
      {!replyOpen && onReply && (
        <div style={{ padding: "0 14px 8px" }}>
          <span
            style={{
              fontSize: 10,
              color: "var(--c-text-faint)",
              letterSpacing: "0.02em",
            }}
          >
            Click to reply
          </span>
        </div>
      )}
    </div>
  );
}
